// 工具函数
import { requiredRule } from '@/utils/form-valid'
import { formValid } from 'jczh-ui'
import { mapState } from 'vuex'

// 后端api
import { judgeCompanyAlreadyExistedApi } from '@/api/company'

export default {
  props: {
    companyType: {
      type: [String, Number],
      default: ''
    },

    serviceType: {
      type: [String, Number],
      default: ''
    },

    companyNameCache: {
      type: String,
      default: ''
    },

    socialCreditCodeCache: {
      type: String,
      default: ''
    },

    companyInfo: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    // 主体名称是否已被注册的校验
    const validateCompanyName = (rule, value, callback) => {
      if (!value || this.companyNameCache === value) {
        return callback()
      }

      judgeCompanyAlreadyExistedApi({
        companyType: this.companyType,
        companyName: value
      })
        .then(res => {
          if (res?.data?.exist) {
            const name = res?.data?.contactName
            const mobile = res?.data?.contactTel
            callback(new Error(`当前主体名称已被  ${name} ${mobile} 注册`))
          } else {
            callback()
          }
        })
        .catch(err => {
          callback(new Error(err.data.message || '接口请求失败'))
        })
    }

    // 统一社会信用代码，身份证号码的校验
    const socialCreditCodeValidate = (rule, value, callback) => {
      const socialCreditCode = /^[A-Z0-9]{18}$/
      const idCardCode = /^\d{17}[\dA-Z]$/

      if (!socialCreditCode.test(value) && !idCardCode.test(value)) {
        callback(new Error('格式错误'))
        return
      }

      if (this.socialCreditCodeCache === value) {
        return callback()
      }

      judgeCompanyAlreadyExistedApi({
        companyType: this.companyType,
        socialCreditCode: value
      })
        .then(res => {
          if (res?.data?.exist) {
            callback(new Error('当前输入已存在'))
          } else {
            callback()
          }
        })
        .catch(err => {
          callback(new Error(err.data.message || '接口请求失败'))
        })
    }

    // 所属区域的校验
    const regionValidate = (rule, value, callback) => {
      if (value === '中国') {
        if (
          !(
            this.formData.regionInfo.province &&
            this.formData.regionInfo.city &&
            this.formData.regionInfo.district
          )
        ) {
          callback(new Error('请输入所在区域'))
        }
      } else if (value === '国外') {
        callback(new Error('请输入国家名称'))
      }

      callback()
    }

    return {
      // 表单
      formData: {
        // 企业主体信息
        companyName: '',
        socialCreditCode: '',
        nature: '',

        // 法定代表信息
        legalPersonInfo: {
          legalPersonName: '',
          legalPersonIdCard: '',
          legalPersonTel: ''
        },

        // 管理员信息
        administratorInfo: {
          name: '',
          email: ''
        },

        // 联系人信息
        contactInfo: {
          contactName: '',
          contactTel: '',
          contactEmail: ''
        },

        // 地区信息
        regionInfo: {
          region: '',
          province: '',
          city: '',
          district: '',
          address: ''
        },

        // 照片信息
        photoInfo: {
          businessLicense: '',
          legalPersonPowerOfAttorney: '',
          bankLicense: '',
          idCardFront: '',
          idCardReverse: '',
          idCardHandHeld: ''
        },

        // 银行信息
        bankInfo: {
          bank: '',
          bankAccount: ''
        }
      },

      // 还未修改时，formData的数据, JSON格式
      formDataOriginal: '',

      // 表单校验规则
      formRules: {
        // 主体信息
        companyType: requiredRule('请选择认证的主体类型'),
        companyName: [
          requiredRule('请输入主体名称'),
          { validator: validateCompanyName, trigger: ['blur'] }
        ],
        socialCreditCode: [
          requiredRule('请输入'),
          {
            validator: socialCreditCodeValidate,
            trigger: ['blur']
          }
        ],
        nature: requiredRule('请选择'),

        // 法定代表人信息
        'legalPersonInfo.legalPersonName': requiredRule('请输入法定代表人姓名'),

        // 管理员信息
        'administratorInfo.name': requiredRule('请输入管理员姓名'),
        'administratorInfo.email': [
          requiredRule('请输入管理员邮箱'),
          formValid.emailRegular
        ],

        // 联系人信息
        'contactInfo.contactName': requiredRule('请输入联系人姓名'),
        'contactInfo.contactTel': [
          requiredRule('请输入联系人电话号码'),
          formValid.mobileRegular
        ],
        'contactInfo.contactEmail': [
          requiredRule('请输入联系人邮箱'),
          formValid.emailRegular
        ],

        // 地址信息
        'regionInfo.region': [
          requiredRule('请选择国别/地区'),
          {
            validator: regionValidate,
            trigger: ['blur', 'change']
          }
        ],
        'regionInfo.address': requiredRule('请输入地址'),

        // 照片信息
        'photoInfo.businessLicense': requiredRule('请上传'),
        'photoInfo.legalPersonPowerOfAttorney': requiredRule('请上传'),
        'photoInfo.bankLicense': requiredRule('请上传'),
        'photoInfo.idCardFront': requiredRule('请上传'),
        'photoInfo.idCardReverse': requiredRule('请上传'),
        'photoInfo.idCardHandHeld': requiredRule('请上传'),

        // 银行信息
        'bankInfo.bank': requiredRule('请输入开户行名称'),
        'bankInfo.bankAccount': [
          requiredRule('请输入银行账号'),
          requiredRule('企业银行账号只能包含数字', /^\d+$/)
        ]
      }
    }
  },

  computed: {
    ...mapState('user', ['userMobile']),

    address: {
      get() {
        return {
          country: this.formData.regionInfo.region,
          province: this.formData.regionInfo.province,
          city: this.formData.regionInfo.city,
          county: this.formData.regionInfo.district,
          foreign:
            this.formData.regionInfo.region !== '中国'
              ? this.formData.regionInfo.region
              : ''
        }
      },
      set(value) {
        const { country, province, city, county, foreign } = value

        this.formData.regionInfo.region = country
        this.formData.regionInfo.province = province
        this.formData.regionInfo.city = city
        this.formData.regionInfo.district = county

        if (foreign) {
          this.formData.regionInfo.region = foreign
        }
      }
    }
  },

  watch: {
    companyInfo: {
      handler: function(val) {
        this.formData = Object.assign({}, this.formData, val)
      },
      immediate: true
    },

    'formData.administratorInfo.name': {
      handler: function(newVal, oldVal) {
        if (this.formData.contactInfo.contactName === oldVal) {
          this.formData.contactInfo.contactName = newVal
        }
      },
      immediate: true
    },
    'formData.administratorInfo.email': {
      handler: function(newVal, oldVal) {
        if (this.formData.contactInfo.contactEmail === oldVal) {
          this.formData.contactInfo.contactEmail = newVal
        }
      },
      immediate: true
    },
    userMobile: {
      handler: function(val) {
        if (!this.formData.contactInfo.contactTel) {
          this.formData.contactInfo.contactTel = val
        }
      },
      immediate: true
    }
  },

  created() {
    this.formDataOriginal = JSON.stringify(this.formData)
  },

  methods: {
    checkFormData() {
      return this.$refs.certificationForm.validate()
    }
  }
}
